import { inputTypeLabels } from "@/constants";

export const generateRandomString = (length: number) =>
  Array(length)
    .fill(null)
    .map(() => Math.random().toString(36).substr(2))
    .join("")
    .slice(0, length);

/**
 * Generate a 32 chars randomId (useful for dynamic Ids in UI)
 */
export const generateRandomId = (): string => generateRandomString(32);

export const isSelectInput = (inputType: string): boolean => inputType === "select";
export const isMultiSelectInput = (inputType: string): boolean => inputType === "multiselect";

/**
 * We have the following attribute type formats:
 *  - the server format (ie: string[])
 *  - the frontend format or input format (ie: multiselect)
 */

/**
 * Returns the form field type from the class attribute type:
 *
 *  [server attribute type] --> [frontend input type]
 *
 * @param {string} attributeType the type of the class attribute
 * @param {boolean} hasAvailableValues wether the attribute is a select with available values
 */
export const getInputTypeFromAttributeType = (attributeType: string, hasAvailableValues = false): string => {
  if (attributeType === "string[]" && hasAvailableValues) {
    return "multiselect";
  }

  if (attributeType === "string" && hasAvailableValues) {
    return "select";
  }

  return attributeType;
};

/**
 * Returns a human readable label for the attribute type
 * @param {string} attributeType the type of the class attribute
 * @param {boolean} hasAvailableValues wether the attribute is a select with available values
 */
export const getAttributeTypeLabel = (attributeType: string, hasAvailableValues = false): string => {
  const inputType = getInputTypeFromAttributeType(attributeType, hasAvailableValues);
  return inputTypeLabels[inputType as never];
};

/**
 * Returns true if the attribute type returned by the server is an array (ie: string[], integer[] or float[])
 * @param {string} attributeType type returned by the server
 */
export const isArrayAttributeType = (type: string): boolean =>
  ["multiselect", "tags", "string[]", "integer[]", "float[]"].includes(type);

/**
 * return a random integer between min and max both inclusive;
 * @param {*} min
 * @param {*} max
 */
export const getRandom = (min: number, max: number): number => Math.floor(Math.random() * (max - min + 1)) + min;

// type ObjectifyInput = { [key: string]: ObjectifyInput[] | AttributeValue };
// type ObjectifyResult = { [key: string]: ObjectifyResult | AttributeValue };
// type ObjectifyConfig<K> = [keyof K, keyof K, ObjectifyConfig<K[keyof K]>?];

// function objectifyArray<Item extends ObjectifyInput, ItemKey extends keyof Item>(array: Item[], config: ObjectifyConfig<Item>) {
//   return Object.fromEntries(
//     array.map((item) => {
//       const [key, value, children] = config;
//       let body: ObjectifyResult | AttributeValue;
//       if (children) {
//         const newArray = item[value] as Item[ItemKey];
//         if (!Array.isArray(newArray)) {
//           throw new Error("the config doesn't match the structure");
//         }
//         body = objectifyArray(newArray, children);
//       } else {
//         body = item[value] as AttributeValue;
//       }
//       const newKey = item[key] as string;
//       return [newKey, body];
//     })
//   );
// }

/*
 * the function objectifyArray takes an array of object and a configuration and returns
 * an object of objects.
 *
 * Example:
 * const myArray = [{foo: "hello", values: [{ bar: "hi", instances: [{baz: "greetings", foo: "world"}]}]}]
 *
 * objectifyArray(myArray, ["foo", "values", ["bar", "instances", ["baz", "foo"]]]) will return
 *
 * { "hello": { "hi": {"greetings": "world"}}}
 *
 *
 * Another example:
 * Original Object:
 * [{
 *   technicalName: "class-name",
 *   instances: [{
 *     id: "instanceId",
 *     values: [{
 *       technicalName: "attrTechName",
 *       value: "value",
 *       isOverridden: true,
 *     }]
 *   }]
 * }]
 *
 * Request(second parameter of the function):
 * ["technicalName", "instances", ["id", "values", ["technicalName", "*"]]]
 *
 * Result:
 * "class-name": {
 *   "instanceId": {
 *     "attrTechName": {
 *       value: "value",
 *       isOverridden: true
 *     }
 *   }
 * }
 *
 * @param array The array of objects you want to "objectify"
 * @param config The config of parameters that have to become keys:
 *  - First item in the config should be a key: its actual value will become the key of the created object
 *  - Second item is also a key that represent the next array to parse
 *  - Third item will be the recursive argument as an array of config
 *
 * @returns
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function objectifyArray(array: any[], config: any[]): any {
  return Object.fromEntries(
    array.map((item) => {
      const [key, value, children] = config;
      let body;
      if (value === "*") {
        body = item;
      } else if (children) {
        const newArray = item[value];
        if (!Array.isArray(newArray)) {
          throw new Error("the config doesn't match the structure");
        }
        body = objectifyArray(newArray, children);
      } else {
        body = item[value];
      }
      const newKey = item[key] as string;
      return [newKey, body];
    })
  );
}
